<template>
  <FormComponent
    title="Bonus Variance Report"
    :nonFieldErrors="formErrors.non_field_errors"
  >
    <template #formActions> </template>

    <template #formContent>
      <v-form v-model="isValid" ref="VarianceForm">
        <v-card class="rounded-lg mb-3 mt-2 pb-3 ml-2 mr-2" variant="outlined">
          <v-card-title class="ml-1 pl-1 font-weight-bold">
            <v-row>
              <v-col cols="5"> Check Variance </v-col>
              <v-col cols="7" class="d-flex justify-end">
                <v-btn
                  color="primary"
                  depressed
                  small
                  :disabled="!isValid"
                  @click="submitForm()"
                >
                  Submit
                </v-btn>

                <v-btn
                  color="secondary"
                  class="ml-3"
                  depressed
                  small
                  @click="resetForm()"
                >
                  Reset
                </v-btn>
              </v-col>
            </v-row>
          </v-card-title>
          <v-row class="ma-0">
            <v-col cols="12" lg="4" class="Form-Columns-Dense">
              <DateField
                ref="StartDateField"
                :inputProps="{
                  dense: true,
                  label: 'Trip Completion Start Date *',
                  clearable: true,
                  rules: [
                    (val) => !!val || 'Trip Completion Start Date is Required!',
                  ],
                  'hide-details': 'auto',
                  'error-messages': formErrors.start_date,
                }"
                v-model="filters.start_date"
                @change="delete formErrors.start_date"
              />
            </v-col>
            <v-col cols="12" lg="4" class="Form-Columns-Dense">
              <DateField
                ref="EndDateField"
                :inputProps="{
                  dense: true,
                  label: 'Trip Completion End Date *',
                  clearable: true,
                  rules: [
                    (val) => !!val || 'Trip Completion End Date is Required!',
                  ],
                  'hide-details': 'auto',
                  'error-messages': formErrors.end_date,
                }"
                :min="filters.start_date"
                v-model="filters.end_date"
                @change="delete formErrors.end_date"
              />
            </v-col>
            <v-col cols="12" lg="4" class="Form-Columns-Dense">
              <MultiSelect
                label="Depot "
                name="bonus-variance-form-depot"
                :returnObject="false"
                :items="allDepot"
                itemText="depot_name"
                itemValue="id"
                hide-details="auto"
                v-model="filters.depot"
                :error-messages="formErrors.depot"
                @input="delete formErrors.depot"
              ></MultiSelect>
            </v-col>
          </v-row>
        </v-card>

        <v-card-text class="mt-3" v-if="hasSearched">
          <v-row class="mb-1" dense>
            <v-col cols="12" sm="4">
              <v-card class="rounded-lg pa-3" variant="outlined">
                <div class="text-caption grey--text">Drivers with variance</div>
                <div class="text-h5 font-weight-bold">
                  {{ summaryList.length }}
                </div>
              </v-card>
            </v-col>
            <v-col cols="12" sm="4">
              <v-card class="rounded-lg pa-3" variant="outlined">
                <div class="text-caption grey--text">
                  Trip Model higher (owed to driver)
                </div>
                <div class="text-h5 font-weight-bold success--text">
                  +{{ positiveVarianceTotal }}
                </div>
              </v-card>
            </v-col>
            <v-col cols="12" sm="4">
              <v-card class="rounded-lg pa-3" variant="outlined">
                <div class="text-caption grey--text">
                  SAP Report higher (overpaid)
                </div>
                <div class="text-h5 font-weight-bold error--text">
                  {{ negativeVarianceTotal }}
                </div>
              </v-card>
            </v-col>
          </v-row>

          <v-alert
            v-if="!summaryList.length"
            type="success"
            text
            dense
            class="mt-3"
          >
            No variance found between Trip and Driver Lease Trip Incentive for
            this date range.
          </v-alert>

          <v-card
            v-else
            class="rounded-lg mt-3"
            variant="outlined"
          >
            <v-simple-table dense fixed-header height="320px">
              <template #default>
                <thead>
                  <tr>
                    <th>Employee ID</th>
                    <th>Driver Name</th>
                    <th class="text-right">Trip Model Total</th>
                    <th class="text-right">Driver Lease Trip Incentive Total</th>
                    <th class="text-right">Variance</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in summaryList"
                    :key="row.employee_id"
                    class="variance-row"
                    :class="{
                      'variance-row--selected':
                        row.employee_id === selectedDriverEmpId,
                    }"
                    @click="selectDriver(row)"
                  >
                    <td>{{ row.employee_id }}</td>
                    <td>{{ row.driver_name }}</td>
                    <td class="text-right">{{ row.trip_model_total }}</td>
                    <td class="text-right">
                      {{ row.driver_lease_trip_incentive_total }}
                    </td>
                    <td
                      class="text-right font-weight-bold"
                      :class="row.variance > 0 ? 'success--text' : 'error--text'"
                    >
                      {{ row.variance > 0 ? "+" : "" }}{{ row.variance }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>

          <v-row v-if="selectedDriverEmpId">
            <v-col cols="12" class="font-weight-bold mt-2">
              Trip-level detail — {{ selectedDriverName }} ({{ selectedDriverEmpId }})
              <span class="text-caption grey--text ml-1">
                {{ selectedDriverMismatches.length }} trip(s)
              </span>
            </v-col>
            <v-col cols="12" v-if="!selectedDriverMismatches.length">
              <v-alert type="info" dense text>
                No trip-level mismatch found for this driver in the selected
                range.
              </v-alert>
            </v-col>
            <v-col
              cols="12"
              md="6"
              lg="4"
              v-for="row in selectedDriverMismatches"
              :key="`${row.category}-${row.trip_id}`"
            >
              <v-card class="rounded-lg" variant="outlined">
                <v-card-title class="d-flex justify-space-between align-center py-2">
                  <span class="text-subtitle-2 font-weight-bold">{{
                    row.trip_code || "N/A"
                  }}</span>
                  <v-chip
                    small
                    :color="categoryColor(row.category)"
                    text-color="white"
                  >
                    {{ categoryLabel(row.category) }}
                  </v-chip>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pt-3">
                  <v-row dense>
                    <v-col cols="6" class="text-caption grey--text">
                      Trip Driver
                    </v-col>
                    <v-col cols="6" class="text-caption text-right">
                      {{ row.driver_name || "N/A" }}
                    </v-col>

                    <v-col cols="6" class="text-caption grey--text">
                      Incentive Driver
                    </v-col>
                    <v-col cols="6" class="text-caption text-right">
                      {{ row.incentive_driver_name || "N/A" }}
                    </v-col>

                    <v-col cols="6" class="text-caption grey--text">
                      Trip End Date (Trip)
                    </v-col>
                    <v-col cols="6" class="text-caption text-right">
                      {{ formatDateTime(row.trip_end) }}
                    </v-col>

                    <v-col cols="6" class="text-caption grey--text">
                      Trip Completed Date (Incentive)
                    </v-col>
                    <v-col cols="6" class="text-caption text-right">
                      {{ formatDateTime(row.incentive_trip_completed_date) }}
                    </v-col>

                    <v-col cols="6" class="text-caption grey--text">
                      Completion Agent
                    </v-col>
                    <v-col cols="6" class="text-caption text-right">
                      {{ row.completion_agent || "N/A" }}
                    </v-col>

                    <v-col cols="6" class="text-caption grey--text">
                      Bonus
                    </v-col>
                    <v-col cols="6" class="text-caption text-right font-weight-bold">
                      {{ row.bonus }}
                    </v-col>
                  </v-row>
                  <v-divider class="my-2"></v-divider>
                  <div class="text-caption grey--text">{{ row.detail }}</div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </v-form>
    </template>
  </FormComponent>
</template>

<script>
import MultiSelect from "@/components/FormBaseComponents/MultiSelect.vue";
import DateField from "@/components/FormBaseComponents/DateField.vue";
import FormComponent from "@/components/General/FormComponent.vue";
import { isAuthenticated, getPermissions } from "@/utils/functions";

const CATEGORY_LABELS = {
  wrong_driver: "Wrong driver tagged",
  missing_incentive: "Incentive record missing",
  date_mismatch: "Completion date/status mismatch",
};

export default {
  components: {
    FormComponent,
    MultiSelect,
    DateField,
  },
  data() {
    return {
      allDepot: [],
      isValid: true,
      formErrors: {},
      filters: {},
      hasSearched: false,
      summaryList: [],
      mismatches: [],
      selectedDriverEmpId: null,
      selectedDriverName: null,
    };
  },
  computed: {
    selectedDriverMismatches() {
      if (!this.selectedDriverEmpId) return [];
      return this.mismatches.filter(
        (row) =>
          row.driver_employee_id === this.selectedDriverEmpId ||
          row.incentive_employee_id === this.selectedDriverEmpId
      );
    },
    positiveVarianceTotal() {
      return this.summaryList
        .filter((row) => row.variance > 0)
        .reduce((sum, row) => sum + Number(row.variance), 0);
    },
    negativeVarianceTotal() {
      return this.summaryList
        .filter((row) => row.variance < 0)
        .reduce((sum, row) => sum + Number(row.variance), 0);
    },
  },
  methods: {
    selectDriver(row) {
      this.selectedDriverEmpId = row.employee_id;
      this.selectedDriverName = row.driver_name;
    },
    categoryLabel(category) {
      return CATEGORY_LABELS[category] || category;
    },
    categoryColor(category) {
      const colors = {
        wrong_driver: "deep-orange",
        missing_incentive: "red",
        date_mismatch: "blue-grey",
      };
      return colors[category] || "grey";
    },
    formatDateTime(value) {
      if (!value) return "N/A";
      return this.$globals.formatDate(value);
    },
    getDepotList() {
      this.$api.depot
        .getDepotList({ limit: "all" })
        .then((res) => {
          this.allDepot = res.data;
        })
        .catch((err) => {
          console.error(err);
        });
    },
    submitForm() {
      if (!this.filters.start_date || !this.filters.end_date) {
        return;
      }
      this.$bus.$emit("showLoader", true);
      this.selectedDriverEmpId = null;
      this.selectedDriverName = null;

      this.$api.sapBonusPayout
        .getBonusVarianceReport(this.filters)
        .then((res) => {
          this.$bus.$emit("showLoader", false);
          this.hasSearched = true;
          const data = res.data || res;
          this.summaryList = data.summary || [];
          this.mismatches = data.mismatches || [];
        })
        .catch((err) => {
          this.$bus.$emit("showLoader", false);
          this.$bus.$emit("showToastMessage", {
            message:
              (err.data && err.data.message) ||
              "Couldn't fetch bonus variance report",
            color: "error",
          });
        });
    },
    resetForm() {
      if (this.$refs.VarianceForm) {
        this.$refs.VarianceForm.reset();
      }
      this.filters = {};
      this.hasSearched = false;
      this.summaryList = [];
      this.mismatches = [];
      this.selectedDriverEmpId = null;
      this.selectedDriverName = null;
    },
  },
  mounted() {
    this.getDepotList();
  },
  beforeMount() {
    if (!isAuthenticated()) {
      this.$router.push("/");
    } else {
      const permissions = getPermissions("special_edit");
      if (!permissions || !permissions.view) {
        this.$bus.$emit("showToastMessage", {
          message: "You don't have access to this page",
          color: "error",
        });
        this.$router.push(
          `/app/${
            localStorage.getItem("user_type")
              ? localStorage.getItem("user_type").toLowerCase()
              : "error"
          }`
        );
      }
    }
  },
};
</script>

<style scoped>
.variance-row {
  cursor: pointer;
}
.variance-row:hover {
  background-color: rgba(0, 0, 0, 0.04);
}
.variance-row--selected {
  background-color: rgba(240, 112, 37, 0.08);
}
</style>
